import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  IconButton,
} from "@mui/material";
import { Close, AccountCircle, Delete } from "@mui/icons-material";

export const ProfilePictureDialog = ({
  open,
  onClose,
  onUpload,
  onDelete,
  hasProfilePicture,
}) => {
  const [dragging, setDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  const handleFileSelect = (file) => {
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      return;
    }

    setSelectedFile(file);
    setPreviewUrl(URL.createObjectURL(file));
  };

  const handleFileUpload = (event) => {
    const file = event.target.files?.[0];
    handleFileSelect(file);
  };

  const handleDrop = (event) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    handleFileSelect(file);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleUploadClick = async () => {
    if (selectedFile) {
      await onUpload(selectedFile);
      handleClose();
    }
  };

  const handleDeleteClick = async () => {
    await onDelete();
    handleClose();
  };

  const handleClose = () => {
    setSelectedFile(null);
    setPreviewUrl(null);
    setDragging(false);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" justifyContent="space-between" alignItems="center">
          <Typography variant="h6">Profile Picture</Typography>
          <IconButton onClick={handleClose} size="small">
            <Close />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3}>
          <Box
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            sx={{
              border: "2px dashed",
              borderColor: dragging ? "divider" : "primary.main",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              cursor: "pointer",
              backgroundColor: dragging ? "action.hover" : "transparent",
              transition: "background-color 0.2s",
            }}
          >
            <Stack spacing={2} alignItems="center">
              {previewUrl ? (
                <Box
                  component="img"
                  src={previewUrl}
                  alt="Preview"
                  sx={{
                    width: 150,
                    height: 150,
                    borderRadius: "50%",
                    objectFit: "cover",
                  }}
                />
              ) : (
                <Box sx={{ display: "flex", gap: 1, color: "text.secondary" }}>
                  <AccountCircle sx={{ fontSize: 80 }} />
                </Box>
              )}
              <Typography variant="subtitle1" color="text.secondary">
                {selectedFile
                  ? selectedFile.name
                  : "Choose a file or drag and drop it here"}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Accepted formats: JPEG, PNG. Max file size 5MB.
              </Typography>

              <Button
                variant="contained"
                component="label"
                sx={{ textTransform: "none" }}
              >
                Choose File
                <input
                  type="file"
                  hidden
                  accept="image/*"
                  onChange={handleFileUpload}
                />
              </Button>
            </Stack>
          </Box>

          {hasProfilePicture && (
            <Box>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                Or remove your current profile picture
              </Typography>
              <Button
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={handleDeleteClick}
                fullWidth
                sx={{ textTransform: "none" }}
              >
                Delete Profile Picture
              </Button>
            </Box>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} sx={{ textTransform: "none" }}>
          Cancel
        </Button>
        <Button
          onClick={handleUploadClick}
          variant="contained"
          disabled={!selectedFile}
          sx={{ textTransform: "none" }}
        >
          Upload
        </Button>
      </DialogActions>
    </Dialog>
  );
};
